import type { Session } from "@supabase/supabase-js";
import { createContext, useContext, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { authConfigured, supabase } from "../lib/supabase";

export interface Auth { userId: string | null; email: string | null; getToken(): Promise<string | null>; signOut(): void | Promise<void> }
export const AuthCtx = createContext<Auth>({ userId: null, email: null, getToken: async () => null, signOut() {} });
export const useAuth = () => useContext(AuthCtx);

/** Renders `children` only once the visitor has a Supabase session. Sign-in is by email and password. */
export function AuthGate({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null | undefined>(authConfigured ? undefined : null);

  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = supabase.auth.onAuthStateChange((_event, next) => setSession(next));
    return () => data.subscription.unsubscribe();
  }, []);

  const userId = session?.user.id ?? null;
  const email = session?.user.email ?? null;
  // Stable across token refreshes so consumers do not restart their work when the access token rotates.
  const auth = useMemo<Auth>(() => ({
    userId,
    email,
    getToken: async () => (await supabase!.auth.getSession()).data.session?.access_token ?? null,
    signOut: () => void supabase!.auth.signOut(),
  }), [userId, email]);

  if (!supabase) {
    return <AuthMessage title="Sign-in isn’t configured.">Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY (see .env.example), then restart the dev server.</AuthMessage>;
  }
  if (session === undefined) return <p className="loading">Loading…</p>;
  if (!session) return <SignIn />;
  return (
    <AuthCtx.Provider value={auth}>
      {children}
    </AuthCtx.Provider>
  );
}

function AuthMessage({ title, children }: { title: string; children: ReactNode }) {
  return <main className="empty-state"><h1>{title}</h1><p>{children}</p></main>;
}

function SignIn() {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const signingIn = mode === "signin";

  // A session fires onAuthStateChange, which swaps this screen for the app.
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    const credentials = { email: email.trim(), password };
    const { data, error } = signingIn
      ? await supabase!.auth.signInWithPassword(credentials)
      : await supabase!.auth.signUp(credentials);
    setBusy(false);
    if (error) setError(error.message);
    else if (!data.session) {
      // Only when the project requires email confirmation.
      setNotice(`Confirm your email address using the link we sent to ${credentials.email}, then sign in.`);
      setMode("signin");
    }
  }

  return (
    <main className="empty-state auth-card">
      <h1>{signingIn ? "Sign in to Hibiki" : "Create your Hibiki account"}</h1>
      <form onSubmit={submit}>
        <label htmlFor="auth-email" className="sr-only">Email</label>
        <input id="auth-email" type="email" required autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <label htmlFor="auth-password" className="sr-only">Password</label>
        <input id="auth-password" type="password" required minLength={8} autoComplete={signingIn ? "current-password" : "new-password"} placeholder="Password (8+ characters)" value={password} onChange={(e) => setPassword(e.target.value)} />
        <button className="button" disabled={busy}>{busy ? "Please wait…" : signingIn ? "Sign in" : "Create account"}</button>
        <button type="button" className="text-link" onClick={() => { setMode(signingIn ? "signup" : "signin"); setError(null); setNotice(null); }}>
          {signingIn ? "New here? Create an account" : "Have an account? Sign in"}
        </button>
      </form>
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
    </main>
  );
}
